/**
 * Plan management utilities - save, load, list plans
 */
import * as authUtils from './auth';
import { BACKEND_URL } from './backendUrl';

let currentPlanId = null;

const getPlanAuthHeader = async () => {
  if (authUtils.getFreshAuthHeader) {
    return authUtils.getFreshAuthHeader();
  }
  return authUtils.getAuthHeader();
};

const dispatchSessionExpired = () => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('craftmyparty:session-expired'));
  } catch (_) {}
};

const fetchWithPlanAuth = async (url, options = {}, retry = true) => {
  const authHeader = await getPlanAuthHeader();
  const response = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...authHeader,
    },
  });

  if (response.status === 401) {
    if (retry && authUtils.getRefreshToken()) {
      try {
        await authUtils.refreshAccessToken();
        return fetchWithPlanAuth(url, options, false);
      } catch (_) {
        // Refresh failed — let the central session-expired flow handle UX
        // (AuthContext / App.js show the auth modal and preserve recovery).
        dispatchSessionExpired();
      }
    } else {
      // No refresh token or retry disabled — dispatch session expired immediately!
      dispatchSessionExpired();
    }
  }

  return response;
};

/**
 * Get current plan ID from session
 */
export const getCurrentPlanId = () => {
  return currentPlanId || sessionStorage.getItem('current_plan_id');
};

/**
 * Set current plan ID
 */
export const setCurrentPlanId = (planId) => {
  currentPlanId = planId;
  if (planId) {
    sessionStorage.setItem('current_plan_id', planId);
  } else {
    sessionStorage.removeItem('current_plan_id');
  }
};

/**
 * Generate a unique plan name by checking existing plans
 */
const generateUniquePlanName = async (baseName) => {
  try {
    const existingPlans = await getPlans();
    // Check both plan_name and event_type to avoid duplicates
    const existingNames = new Set();
    existingPlans.forEach(p => {
      const planName = p.event_data?.plan_name;
      const eventType = p.event_data?.event_type;
      if (planName) existingNames.add(planName);
      if (eventType) existingNames.add(eventType);
    });

    // If base name doesn't exist, use it
    if (!existingNames.has(baseName)) {
      return baseName;
    }

    // Find the next available number
    let number = 2;
    let newName = `${baseName} ${number}`;

    while (existingNames.has(newName)) {
      number++;
      newName = `${baseName} ${number}`;
    }

    return newName;
  } catch (error) {
    // If we can't fetch plans, just return base name
    console.error('Error generating unique plan name:', error);
    return baseName;
  }
};

/**
 * Create a new plan
 */
export const createPlan = async (planData) => {
  // Check authentication first
  if (!authUtils.isAuthenticated()) {
    throw new Error('Not authenticated');
  }

  const authHeader = await getPlanAuthHeader();
  const token = authUtils.getAccessToken();
  if (!token || !token.trim()) {
    throw new Error('Not authenticated');
  }

  if (!authHeader.Authorization || !authHeader.Authorization.trim()) {
    throw new Error('Not authenticated');
  }

  // Ensure unique plan name
  let baseName = planData.event_data?.plan_name || '';

  if (!baseName) {
    const rawEventName = (planData.event_data?.custom_event_type || planData.event_data?.event_type || 'Untitled Event').trim();
    const focus = Array.isArray(planData.event_data?.focus_selection) ? planData.event_data.focus_selection : [];
    
    let suffix = '';
    if (focus.includes('everything') || focus.length === 0) {
      suffix = '';
    } else if (focus.length === 1) {
      const singleFocus = focus[0];
      const suffixes = {
        food: 'Food & Drinks Plan',
        activities: 'Activity Plan',
        decor: 'Decor Plan',
        shopping: 'Shopping List',
        theme_ideas: 'Theme Idea'
      };
      suffix = suffixes[singleFocus] ? ` ${suffixes[singleFocus]}` : ' Plan';
    } else {
      suffix = ' Custom Plan';
    }

    if (suffix) {
      const cleanSuffix = suffix.trim();
      if (rawEventName.endsWith(cleanSuffix)) {
        baseName = rawEventName;
      } else {
        baseName = `${rawEventName}${suffix}`;
      }
    } else {
      baseName = rawEventName;
    }
  }

  const uniqueName = await generateUniquePlanName(baseName);

  // Update plan data with unique name
  const planDataWithUniqueName = {
    ...planData,
    event_data: {
      ...planData.event_data,
      plan_name: uniqueName,
      event_type: planData.event_data?.event_type // Keep original event_type
    }
  };

  const response = await fetchWithPlanAuth(`${BACKEND_URL}/api/plans`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(planDataWithUniqueName),
  });

  if (!response.ok) {
    if (response.status === 401) {
      dispatchSessionExpired();
      throw new Error('Not authenticated');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create plan');
  }

  const plan = await response.json();
  setCurrentPlanId(plan.plan_id);
  return plan;
};

/**
 * Get all user's plans
 */
export const getPlans = async () => {
  // Check if user is authenticated before making request
  if (!authUtils.isAuthenticated()) {
    // Silently fail - this is expected for unauthenticated users
    return [];
  }

  // Get token directly to ensure it exists and is not empty
  const token = authUtils.getAccessToken();
  if (!token || !token.trim()) {
    // Silently fail - token not available yet
    return [];
  }

  const authHeader = await getPlanAuthHeader();
  if (!authHeader.Authorization || !authHeader.Authorization.trim()) {
    // Silently fail - token not available yet
    return [];
  }

  try {
    const response = await fetchWithPlanAuth(`${BACKEND_URL}/api/plans`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        dispatchSessionExpired();
        throw new Error('Not authenticated');
      }
      // Only throw for non-401 errors
      throw new Error(`Failed to fetch plans: ${response.status} ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    // Network errors or other issues - return empty array
    // Don't log 401 errors as they're handled above and expected in some cases
    if (error.message && !error.message.includes('401') && !error.message.includes('Unauthorized')) {
      console.error('Error fetching plans:', error);
    }
    return [];
  }
};

/**
 * Get plan by ID
 */
export const getPlan = async (planId) => {
  // Get auth header first
  let authHeader = await getPlanAuthHeader();

  // If no auth header, check if we have a refresh token and try to refresh
  if (!authHeader.Authorization || !authHeader.Authorization.trim()) {
    const refreshToken = authUtils.getRefreshToken();
    if (refreshToken) {
      try {
        await authUtils.refreshAccessToken();
        authHeader = authUtils.getAuthHeader();
      } catch (error) {
        // Refresh failed
        throw new Error('Authentication required. Please log in.');
      }
    } else {
      throw new Error('Authentication required. Please log in.');
    }
  }

  const response = await fetchWithPlanAuth(`${BACKEND_URL}/api/plans/${planId}`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      // fetchWithPlanAuth already attempted a refresh and dispatched
      // session-expired if that failed. Throw a recoverable error so the
      // restore flow in App.js can bail out without forcing a logout.
      throw new Error('Authentication failed. Please log in again.');
    }
    const errorText = await response.text();
    throw new Error(`Failed to fetch plan: ${response.status} ${errorText || response.statusText}`);
  }

  const plan = await response.json();
  setCurrentPlanId(planId);
  return plan;
};

/**
 * Lightweight favorite toggle for a plan and/or its per-theme favorites.
 * Avoids resaving the whole plan blob just to flip a star. Either field can
 * be omitted; the server only updates whatever is provided.
 */
export const updatePlanFavorite = async (planId, { isFavorite, favoriteThemeKeys } = {}) => {
  const body = {};
  if (typeof isFavorite === 'boolean') body.is_favorite = isFavorite;
  if (Array.isArray(favoriteThemeKeys)) body.favorite_theme_keys = favoriteThemeKeys;
  if (Object.keys(body).length === 0) return null;

  const response = await fetchWithPlanAuth(`${BACKEND_URL}/api/plans/${planId}/favorite`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Not authenticated');
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to update favorite');
  }
  return response.json();
};

/**
 * Update plan
 */
export const updatePlan = async (planId, planData) => {
  const response = await fetchWithPlanAuth(`${BACKEND_URL}/api/plans/${planId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(planData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update plan');
  }

  return response.json();
};

/**
 * Auto-save plan (lightweight update)
 * For drafts, always create a new plan to allow multiple plans
 */
export const autoSavePlan = async (planData, forceNew = false) => {
  const planId = getCurrentPlanId();

  // If forcing new plan or no plan ID exists, create new plan with unique name
  if (forceNew || !planId) {
    const newPlan = await createPlan(planData);
    return newPlan;
  }

  try {
    const response = await fetchWithPlanAuth(`${BACKEND_URL}/api/plans/${planId}/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(planData),
    });

    if (!response.ok) {
      // If plan doesn't exist, create new one with unique name
      if (response.status === 404) {
        setCurrentPlanId(null);
        return createPlan(planData);
      }
      throw new Error('Failed to save plan');
    }

    return response.json();
  } catch (error) {
    console.error('Auto-save error:', error);
    // If update fails, create new plan with unique name
    setCurrentPlanId(null);
    return createPlan(planData);
  }
};

/**
 * Delete plan
 */
export const deletePlan = async (planId) => {
  // Check authentication first
  if (!authUtils.isAuthenticated()) {
    throw new Error('Not authenticated');
  }

  const token = authUtils.getAccessToken();
  if (!token || !token.trim()) {
    throw new Error('Not authenticated');
  }

  const authHeader = await getPlanAuthHeader();
  if (!authHeader.Authorization || !authHeader.Authorization.trim()) {
    throw new Error('Not authenticated');
  }

  const response = await fetchWithPlanAuth(`${BACKEND_URL}/api/plans/${planId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      dispatchSessionExpired();
      throw new Error('Not authenticated');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete plan');
  }

  if (getCurrentPlanId() === planId) {
    setCurrentPlanId(null);
  }

  return response.json();
};


/**
 * Get plans with separated shared plans
 * Currently performs client-side filtering
 */
export const getPlansWithShares = async () => {
  const allPlans = await getPlans();

  // Assuming 'is_owner' flag exists or logic to determine ownership
  // If not explicitly 'false', assume owner for backward compatibility
  const plans = allPlans.filter(p => p.is_owner !== false);
  const shared_plans = allPlans.filter(p => p.is_owner === false);

  return { plans, shared_plans };
};
